import React, { useState, useEffect } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore.js";
import { StripeApplePayProvider } from "@/utils/applePay/applePayProvider";

const ApplePayButton = ({ height, onApplePaySubmit, requirePolicy }) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [provider, setProvider] = useState(null);
  const [paymentProvider, setPaymentProvider] = useState(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [formData, setFormData] = useState({});
  const { paymentsConfig, country_code, currency_code } = useProductData();
  const { selectedProduct, warrantyState, cartTotal } = useCartData();
  const { stripeApplePayAllowed, stripePublisherKey, dynamicDescriptor, applePayConfigured } = paymentsConfig || {};
  const setIsApplePayAvailable = useTemplateStore((s) => s.setIsApplePayAvailable); // Browser defines this
  const handlePaymentMethodChange = useTemplateStore((s) => s.handlePaymentMethodChange);

  // Set provider
  useEffect(() => {
    if (stripeApplePayAllowed) {
      setPaymentProvider("stripe");
      return;
    }

    if (applePayConfigured) {
      setPaymentProvider("adyen");
      return;
    }
  }, [stripeApplePayAllowed, applePayConfigured]);

  // Stripe provider
  useEffect(() => {
    if (paymentProvider !== "stripe") {
      return;
    }

    const initializeProvider = async () => {
      try {
        const applePayProvider = new StripeApplePayProvider(stripePublisherKey);

        await applePayProvider.initialize(selectedProduct, country_code, currency_code);

        const canMake = await applePayProvider.canMakePayment();

        if (canMake && stripeApplePayAllowed) {
          setCanMakePayment(true);
          setIsApplePayAvailable(true);
          setIsAvailable(true);
          setProvider(applePayProvider);

          applePayProvider.onToken(async (event) => {
            setProcessing(true);
            try {
              const payerName = event.payerName || "";
              const nameParts = payerName.split(" ");
              const firstName = nameParts[0] || "";
              const lastName = nameParts.slice(1).join(" ") || nameParts[0] || "";

              const shippingAddress = event.shippingAddress;
              if (!shippingAddress) {
                setProcessing(false);
                return;
              }

              const updatedData = {
                ...formData,
                paymentMethod: "applepay",
                applePayProvider: "stripe",
                apple_pay_token: JSON.stringify(event.token),
                customer: {
                  first_name: firstName,
                  last_name: lastName,
                  email: event.payerEmail || "",
                  phone: event.payerPhone || "",
                  country: shippingAddress.country || "US",
                  address:
                    `${shippingAddress.addressLine?.[0] || ""}${shippingAddress.addressLine?.[1] ? " " + shippingAddress.addressLine?.[1] : ""}`.trim(),
                  address2: "",
                  city: shippingAddress.city || "",
                  state: shippingAddress.region || shippingAddress.city || "",
                  postalCode: shippingAddress.postalCode || "",
                },
              };

              setFormData(updatedData);
              onApplePaySubmit(updatedData);
              setProcessing(false);
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : "Payment processing error";
              console.log(errorMessage);
              setProcessing(false);
            }
          });
        } else {
          setCanMakePayment(false);
          setProvider(null);
        }
      } catch (error) {
        console.error("Error initializing Apple Pay provider:", error);
      }
    };

    initializeProvider();
  }, [
    selectedProduct,
    country_code,
    currency_code,
    stripeApplePayAllowed,
    stripePublisherKey,
    paymentProvider,
    warrantyState,
    cartTotal,
  ]);

  // Adyen provider
  useEffect(() => {
    if (paymentProvider !== "adyen") {
      return;
    }

    try {
      if (applePayConfigured && window.ApplePaySession && window.ApplePaySession.canMakePayments()) {
        setIsAvailable(true);
        setIsApplePayAvailable(true);
      } else {
        setIsAvailable(false);
      }
    } catch (err) {
      console.error("Apple Pay availability check failed:", err);
      setIsAvailable(false);
    }
  }, [paymentProvider, applePayConfigured]);

  // UI gatekeeper
  if (
    (paymentProvider === "adyen" && !isAvailable) ||
    (paymentProvider === "stripe" && !isAvailable) ||
    paymentProvider === null
  ) {
    return null;
  }

  const handleStripeApplePaySubmit = async () => {
    try {
      await provider.show();
    } catch (error) {
      console.error("Error showing Apple Pay:", error);
      const errorMessage = error instanceof Error ? error.message : "Failed to show Apple Pay";
    }
  };

  const handleAdyenApplePaySubmit = async () => {
    const { currency_code, country_code } = useProductData.getState();
    const { cartTotal } = useCartData.getState();

    const paymentRequest = {
      currencyCode: currency_code || "USD",
      countryCode: country_code || "US",
      total: {
        label: dynamicDescriptor,
        amount: cartTotal || "0.00",
      },

      supportedNetworks: ["visa", "masterCard", "amex", "discover", "JCB"],
      merchantCapabilities: ["supports3DS"],
      requiredShippingContactFields: ["email", "phone", "postalAddress", "name"],
    };

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async (event) => {
      const r = await fetch("/wp-json/checkout/v1/order/applepay/validate", { method: "POST" });
      const merchantSession = await r.json();
      session.completeMerchantValidation(merchantSession);
    };

    session.oncancel = (event) => {
      // TODO: Add cancel handler. Maybe we can show modal or error message and let user know to try different payment method?
    };

    session.onpaymentauthorized = async (event) => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => {
          controller.abort();
        }, 30000);

        const applePayData = {
          apple_pay_token: JSON.stringify(event.payment.token),
          applePayProvider: "adyen",
          customer: {
            first_name: event.payment.shippingContact?.givenName || event.payment.billingContact?.givenName || "",
            last_name: event.payment.shippingContact?.familyName || event.payment.billingContact?.familyName || "",
            phone: event.payment.shippingContact?.phoneNumber || event.payment.billingContact?.phoneNumber || "",
            email: event.payment.shippingContact?.emailAddress || "",
            address: event.payment.shippingContact?.addressLines[0] || "",
            address2: event.payment.shippingContact?.addressLines[1] || "",
            city: event.payment.shippingContact?.locality || "",
            state: event.payment.shippingContact?.administrativeArea || "",
            zip: event.payment.shippingContact?.postalCode || "",
            country: event.payment.shippingContact?.countryCode || "",
          },
        };

        if (onApplePaySubmit) {
          await onApplePaySubmit(applePayData, { signal: controller.signal });
          clearTimeout(timeout);

          session.completePayment(ApplePaySession.STATUS_SUCCESS);
        } else {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
        }
      } catch (e) {
        console.error("Payment authorization error:", e);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      }
    };

    session.begin();
  };

  const handleClick = async () => {
    handlePaymentMethodChange("applepay");

    if (typeof requirePolicy === "function") {
      const ok = await requirePolicy();
      if (!ok) return;
    }

    if (paymentProvider === "adyen") {
      await handleAdyenApplePaySubmit();
    } else if (paymentProvider === "stripe") {
      await handleStripeApplePaySubmit();
    }
  };

  return (
    <div className="applepay-container">
      <button
        type="button"
        onClick={handleClick}
        style={{
          appearance: "-apple-pay-button",
          WebkitAppearance: "-apple-pay-button",
          applePayButtonStyle: "black",
          width: "100%",
          height: height || 56,
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
        }}
      />
    </div>
  );
};

export default ApplePayButton;
